/* hs-sections-b.js — behaviour for hs-review-slider, hs-reviews-list and hs-video-carousel.
   One owner per behaviour (R-36), all delegated or self-initialising per root:
     [data-hs-marquee]  photo strip — clones the first set until the track is 2x the viewport, then
                        animates exactly one set per --mq_s (Resilia's own ts-marquee maths).
     [data-hs-car]      carousel — slides per view / per move are READ from the section CSS
                        (--hs-pv, --hs-group), so CSS media queries are the single source of truth.
                        Loop = cloned sets either side + a silent re-centre after each move.
                        Swipe with pointer events (touch-action:pan-y keeps vertical scroll native).
                        Arrows name a target index (R-43); dots = ceil(slides / group).
                        A video whose slide leaves the view is paused. Click-to-play itself is
                        owned by snippets/hs-video-card (one owner per behaviour, R-36).
     [data-hs-more]     "Read more reviews" toggle (mobile).
     [data-hs-warm]     lazy images inside are switched to eager ~800 px before the section
                        arrives, so tiles never pop in empty (R-42, R-44).
   Nothing here reads layout on the scroll path (R-89): measuring happens on init, debounced
   resize and clicks only. Reduced motion: no slide animation; the marquee is stopped in CSS. */
(function () {
  'use strict';
  if (window.__hsSectionsB) return;
  window.__hsSectionsB = true;

  var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var slice = Array.prototype.slice;
  function $$(root, sel) { return slice.call(root.querySelectorAll(sel)); }
  function mod(a, n) { return ((a % n) + n) % n; }

  /* ------------------------------------------------------------ marquee */
  function initMarquee(strip) {
    var track = strip.querySelector('[data-hs-mq-track]');
    var setLen = parseInt(strip.getAttribute('data-set'), 10) || 0;
    if (!track || !setLen) return;
    $$(track, '[data-hs-mq-clone]').forEach(function (n) { n.parentNode.removeChild(n); });
    var orig = slice.call(track.children, 0, setLen);
    var guard = 30;
    while (track.scrollWidth < window.innerWidth * 2 && guard-- > 0) {
      orig.forEach(function (n) {
        var c = n.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-hs-mq-clone', '');
        c.removeAttribute('data-shopify-editor-block');
        $$(c, 'img').forEach(function (i) { i.alt = ''; });
        track.appendChild(c);
      });
    }
    var sets = track.children.length / setLen;
    track.style.setProperty('--hs-mq-end', '-' + (100 / sets).toFixed(4) + '%');
    strip.__hsW = window.innerWidth;
  }

  /* ------------------------------------------------------------ carousel */
  function Car(root) {
    var vp = root.querySelector('[data-hs-car-vp]');
    var track = root.querySelector('[data-hs-car-track]');
    var dotsEl = root.querySelector('[data-hs-car-dots]');
    var prevBtn = root.querySelector('[data-hs-car-prev]');
    var nextBtn = root.querySelector('[data-hs-car-next]');
    if (!vp || !track) return null;
    var real = slice.call(track.children);
    var n = real.length;
    var loop = root.getAttribute('data-loop') !== 'false';
    var dotLabel = root.getAttribute('data-dot-label') || '';
    var pv = 1, group = 1, cpy = 0, pos = 0, r = 0, step = 0, speed = 300;
    var locked = true, pending = null, lastW = 0;
    var slides = real;

    function readVars() {
      var cs = getComputedStyle(root);
      pv = Math.max(1, parseInt(cs.getPropertyValue('--hs-pv'), 10) || 1);
      group = Math.max(1, parseInt(cs.getPropertyValue('--hs-group'), 10) || 1);
      var d = parseFloat(getComputedStyle(track).transitionDuration);
      speed = isNaN(d) ? 300 : d * 1000;
    }

    function clearClones() {
      $$(track, '[data-hs-clone]').forEach(function (c) { c.parentNode.removeChild(c); });
    }

    function clone(el) {
      var c = el.cloneNode(true);
      c.setAttribute('data-hs-clone', '');
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('data-shopify-editor-block');
      $$(c, '[data-shopify-editor-block]').forEach(function (x) { x.removeAttribute('data-shopify-editor-block'); });
      $$(c, 'a,button,video,[tabindex],input,select,textarea').forEach(function (x) { x.setAttribute('tabindex', '-1'); });
      $$(c, '.is-playing,.has-played,.is-error').forEach(function (x) { x.classList.remove('is-playing', 'has-played', 'is-error'); });
      return c;
    }

    function setup() {
      readVars();
      clearClones();
      lastW = vp.clientWidth;
      vp.scrollLeft = 0; // a pre-JS native swipe may have scrolled the rail
      locked = n <= pv;
      root.classList.toggle('is-locked', locked);
      cpy = 0;
      if (!locked && loop) {
        cpy = Math.max(1, Math.ceil((group + pv - 1) / n));
        var before = document.createDocumentFragment();
        var after = document.createDocumentFragment();
        for (var k = 0; k < cpy; k++) {
          real.forEach(function (el) { before.appendChild(clone(el)); after.appendChild(clone(el)); });
        }
        track.insertBefore(before, track.firstChild);
        track.appendChild(after);
      }
      slides = slice.call(track.children);
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      step = (real[0] ? real[0].getBoundingClientRect().width : 0) + gap;
      if (locked) { pos = 0; r = 0; }
      else if (loop) { r = mod(r, n); pos = cpy * n + r; }
      else { pos = Math.min(Math.max(r, 0), n - pv); r = pos; }
      apply(false);
      buildDots();
      sync();
      root.classList.add('is-ready');
    }

    function apply(animate) {
      track.style.transition = animate && !RM.matches ? '' : 'none';
      track.style.transform = 'translateX(' + (-pos * step) + 'px)';
      if (!animate || RM.matches) {
        void track.offsetWidth; // commit the jump before transitions come back
        track.style.transition = '';
      }
    }

    function dotCount() {
      if (locked) return 0;
      return loop ? Math.ceil(n / group) : Math.ceil((n - pv) / group) + 1;
    }

    function activeDot() {
      if (loop) return Math.floor(r / group);
      return pos >= n - pv ? dotCount() - 1 : Math.floor(pos / group);
    }

    function buildDots() {
      if (!dotsEl) return;
      var count = dotCount();
      if (dotsEl.children.length === count) return;
      dotsEl.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hs-car__dot';
        b.setAttribute('aria-label', (dotLabel ? dotLabel + ' ' : '') + (i + 1));
        b.setAttribute('data-i', String(i));
        dotsEl.appendChild(b);
      }
    }

    function sync() {
      if (dotsEl) {
        var a = activeDot();
        slice.call(dotsEl.children).forEach(function (d, i) {
          if (i === a) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
      }
      if (prevBtn) prevBtn.disabled = !locked && !loop && pos <= 0;
      if (nextBtn) nextBtn.disabled = !locked && !loop && pos >= n - pv;
    }

    /* A video left playing in a slide that has scrolled out of view keeps talking. */
    function pauseHidden() {
      slides.forEach(function (s, i) {
        if (i >= pos && i < pos + pv) return;
        $$(s, 'video').forEach(function (v) { if (!v.paused) v.pause(); });
      });
    }

    function normalize() {
      pending = null;
      if (!loop || locked) return;
      var np = cpy * n + mod(pos - cpy * n, n);
      if (np !== pos) { pos = np; apply(false); }
      pauseHidden();
    }

    function go(target, animate) {
      if (locked) return;
      if (pending) { clearTimeout(pending); normalize(); }
      if (!loop) target = Math.min(Math.max(target, 0), n - pv);
      pos = target;
      r = loop ? mod(pos - cpy * n, n) : pos;
      apply(animate !== false);
      sync();
      if (loop) {
        if (RM.matches || animate === false) normalize();
        else pending = setTimeout(normalize, speed + 40);
      } else {
        pauseHidden();
      }
    }

    function by(dir) {
      if (pending) { clearTimeout(pending); normalize(); }
      go(pos + dir * group);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { by(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { by(1); });
    if (dotsEl) dotsEl.addEventListener('click', function (e) {
      var d = e.target.closest && e.target.closest('.hs-car__dot');
      if (!d) return;
      if (pending) { clearTimeout(pending); normalize(); }
      var k = parseInt(d.getAttribute('data-i'), 10) * group;
      go(loop ? cpy * n + Math.min(k, n - 1) : Math.min(k, n - pv));
    });

    /* Keyboard focus inside a slide that is out of view brings it in. */
    track.addEventListener('focusin', function (e) {
      if (locked) return;
      var s = e.target.closest && e.target.closest('[data-hs-car-slide]');
      var i = slides.indexOf(s);
      if (i < 0 || (i >= pos && i < pos + pv)) return;
      go(loop ? cpy * n + real.indexOf(s) : real.indexOf(s));
      vp.scrollLeft = 0;
    });

    /* ---- swipe / drag */
    var down = false, drag = false, sx = 0, sy = 0, dx = 0, pid = null, justDragged = false;
    vp.addEventListener('pointerdown', function (e) {
      if (locked || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (e.target.closest && e.target.closest('video[controls]')) return; // let the native scrubber work
      if (pending) { clearTimeout(pending); normalize(); }
      down = true; drag = false; sx = e.clientX; sy = e.clientY; dx = 0; pid = e.pointerId;
    });
    vp.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pid) return;
      dx = e.clientX - sx;
      var dy = e.clientY - sy;
      if (!drag) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { down = false; return; }
        drag = true;
        root.classList.add('is-dragging');
        try { vp.setPointerCapture(pid); } catch (err) { /* already released */ }
      }
      var x = -pos * step + dx;
      if (!loop) {
        var min = -(n - pv) * step;
        if (x > 0) x = x / 3; else if (x < min) x = min + (x - min) / 3;
      }
      track.style.transform = 'translateX(' + x + 'px)';
    });
    function end() {
      if (!down) return;
      down = false;
      if (!drag) return;
      drag = false;
      root.classList.remove('is-dragging');
      justDragged = true;
      setTimeout(function () { justDragged = false; }, 60);
      var dist = Math.abs(dx);
      var steps = 0;
      if (dist > Math.min(40, step / 4)) steps = Math.max(group, Math.round(dist / step));
      go(pos + (dx < 0 ? steps : -steps));
    }
    vp.addEventListener('pointerup', end);
    vp.addEventListener('pointercancel', end);
    vp.addEventListener('click', function (e) {
      if (justDragged) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    vp.addEventListener('dragstart', function (e) { e.preventDefault(); });

    setup();
    return {
      root: root,
      resize: function () { if (vp.clientWidth !== lastW) setup(); },
      show: function (el) {
        var s = el.closest && el.closest('[data-hs-car-slide]');
        var i = real.indexOf(s);
        if (i < 0 || locked) return;
        go(loop ? cpy * n + i : i, false);
      }
    };
  }

  /* ------------------------------------------------------------ read more */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-hs-more]');
    if (!btn) return;
    var root = btn.closest('[data-hs-root]');
    if (!root) return;
    var open = btn.getAttribute('aria-expanded') === 'true';
    root.classList.toggle('is-open', !open);
    btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    btn.textContent = open ? btn.getAttribute('data-more') : btn.getAttribute('data-less');
    if (open && btn.scrollIntoView) btn.scrollIntoView({ block: 'nearest' });
  });

  /* ------------------------------------------------------------ pre-warm lazy images */
  var warmIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      warmIO.unobserve(en.target);
      $$(en.target, 'img[loading="lazy"]').forEach(function (i) { i.loading = 'eager'; });
    });
  }, { rootMargin: '800px 0px' }) : null;

  /* ------------------------------------------------------------ init */
  var cars = [];
  function initAll(scope) {
    $$(scope, '[data-hs-marquee]').forEach(initMarquee);
    $$(scope, '[data-hs-car]').forEach(function (el) {
      if (el.__hsCar) return;
      var c = Car(el);
      if (c) { el.__hsCar = c; cars.push(c); }
    });
    if (warmIO) $$(scope, '[data-hs-warm]').forEach(function (el) { warmIO.observe(el); });
  }

  var t = null;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(function () {
      cars = cars.filter(function (c) { return document.body.contains(c.root); });
      cars.forEach(function (c) { c.resize(); });
      $$(document, '[data-hs-marquee]').forEach(function (s) { if (s.__hsW !== window.innerWidth) initMarquee(s); });
    }, 150);
  });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var car = e.target.closest && e.target.closest('[data-hs-car]');
    if (car && car.__hsCar) car.__hsCar.show(e.target);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);
})();
